import { type FC } from 'react';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';
import { formatDistanceToNow } from 'date-fns';
import type { GridReadyEvent, SortChangedEvent } from 'ag-grid-community';

import {
  ButtonField,
  PillSuggestion,
  Tooltip,
  TooltipBody,
} from '@wk/components-react16';
import {
  IconChevronDown,
  IconChevronUp,
  IconHelpCircle,
  IconLoader2,
  IconMinus,
  IconPlayerPlay,
} from '@tabler/icons-react';

import { ENVIRONMENT_ACRONYMS } from '@/config/constants';
import type { Evaluation } from '@/types';
import {
  renderApplicationName,
  renderScore,
  renderSource,
} from '@/modules/utils';

interface ApplicationEvaluationsContentProps {
  isMobileViewport: boolean;
  isLoading: boolean;
  evaluations: Evaluation[];
  onNavigateToApplication: (evaluation: Evaluation) => void;
  onOpenRunEvaluation: (evaluation: Evaluation) => void;
  onGridReady: (event: GridReadyEvent) => void;
  onSortChanged: (event: SortChangedEvent) => void;
}

const ApplicationEvaluationsContent: FC<ApplicationEvaluationsContentProps> = ({
  isMobileViewport,
  isLoading,
  evaluations,
  onNavigateToApplication,
  onOpenRunEvaluation,
  onGridReady,
  onSortChanged,
}) => {
  const getScoreDirection = (evaluation: Evaluation, score: number) => {
    if (
      !evaluation?.previous ||
      evaluation.previous.score === undefined ||
      evaluation.previous.score === null
    ) {
      return null;
    }

    if (score > evaluation.previous.score) {
      return <IconChevronUp size={14} className="text-green-600" />;
    }

    if (score < evaluation.previous.score) {
      return <IconChevronDown size={14} className="text-red-500" />;
    }

    return <IconMinus size={14} className="text-gray-500" />;
  };

  const renderScoreBlock = (
    evaluation: Evaluation,
    type: 'last' | 'upcoming',
  ) => {
    const isUpcoming = type === 'upcoming';
    const checkpoints = isUpcoming
      ? evaluation.totalCheckpoints
      : evaluation.totalScoredCheckpoints;
    const score = isUpcoming ? evaluation.inclusiveScore : evaluation.score;

    if (!evaluation.totalEvaluations) {
      return (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <IconMinus size={14} />
          <span>No evaluations</span>
        </div>
      );
    }

    if (score === null || score === undefined) {
      return <span className="text-sm text-gray-500">N/A</span>;
    }

    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 text-sm font-medium">
          <span>{score}%</span>
          {getScoreDirection(evaluation, score)}
        </div>
        <span className="text-[10px] text-gray-500">
          {checkpoints} Checkpoints
        </span>
      </div>
    );
  };

  if (isMobileViewport) {
    return (
      <>
        <div className="evaluations-cards-container max-sm:pr-1">
          {isLoading ? (
            <div className="flex h-full min-h-[220px] items-center justify-center text-gray-500">
              <IconLoader2 size={22} className="animate-spin" />
            </div>
          ) : evaluations.length === 0 ? (
            <div className="rounded-md border bg-white p-4 text-sm text-gray-600">
              No evaluations found for the current filters.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {evaluations.map((evaluation) => (
                <article
                  key={evaluation.id}
                  className="rounded-lg border bg-white p-3 shadow-sm"
                >
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
                    <button
                      type="button"
                      onClick={() => onNavigateToApplication(evaluation)}
                      className="flex min-w-0 flex-col items-start text-left"
                    >
                      <span className="block w-full break-words text-base font-semibold leading-5">
                        {evaluation.applicationName}
                      </span>
                      <span className="text-xs text-gray-500">
                        {evaluation.bitId}
                      </span>
                    </button>
                    <div className="flex items-center justify-end gap-2 self-start">
                      {evaluation.environmentNames.map((environment) => (
                        <span
                          key={`${evaluation.id}-${environment}`}
                          className={`envBadge envBadge--${environment.toLowerCase()}`}
                        >
                          <PillSuggestion size="small">
                            {ENVIRONMENT_ACRONYMS[
                              environment.toLowerCase() as keyof typeof ENVIRONMENT_ACRONYMS
                            ] ?? environment.slice(0, 3).toLowerCase()}
                          </PillSuggestion>
                        </span>
                      ))}
                      <ButtonField mode="ghost" size="small">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpenRunEvaluation(evaluation);
                          }}
                        >
                          <IconPlayerPlay size={14} />
                        </button>
                      </ButtonField>
                    </div>
                  </div>

                  <dl className="mt-3 grid grid-cols-[minmax(0,0.85fr)_minmax(0,1.35fr)_auto] items-end gap-x-3 gap-y-2">
                    <div>
                      <dt className="text-[10px] uppercase tracking-wide text-gray-500">
                        Division
                      </dt>
                      <dd className="text-sm">
                        {evaluation.divisionName || 'N/A'}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-[10px] uppercase tracking-wide text-gray-500">
                        Last Evaluated At
                      </dt>
                      <dd className="whitespace-nowrap text-sm">
                        {evaluation.generatedAt && evaluation.totalEvaluations
                          ? formatDistanceToNow(new Date(evaluation.generatedAt), {
                              addSuffix: true,
                            })
                          : 'Not Evaluated'}
                      </dd>
                    </div>
                    <div className="justify-self-start text-left">
                      <dt className="text-[10px] uppercase tracking-wide text-gray-500">
                        Evaluations
                      </dt>
                      <dd className="text-sm">
                        {evaluation.totalEvaluations ?? 0}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-wide text-gray-500">
                        Last Score
                      </dt>
                      <dd>{renderScoreBlock(evaluation, 'last')}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-wide text-gray-500">
                        Upcoming Score
                      </dt>
                      <dd>{renderScoreBlock(evaluation, 'upcoming')}</dd>
                    </div>
                    <div className="w-fit justify-self-end self-end">
                      <dt className="sr-only">Source</dt>
                      <dd className="flex items-center" aria-label="Source">
                        {renderSource({
                          data: evaluation,
                        } as CustomCellRendererProps<Evaluation>)}
                      </dd>
                    </div>
                  </dl>
                </article>
              ))}
            </div>
          )}
        </div>
        <ol className="ml-auto flex w-full list-inside list-decimal flex-col gap-2 text-xs text-muted-foreground sm:flex-row sm:gap-8 lg:text-sm">
          <li>Upcoming Score is not available for historical evaluations</li>
          <li>Timeframe for when upcoming score will become current is to be decided</li>
        </ol>
      </>
    );
  }

  return (
    <>
      <div className="evaluations-grid-container w-full max-sm:overflow-x-auto sm:flex-1 sm:min-h-0">
        <div className="h-full max-sm:h-[52vh] max-sm:min-h-[320px] max-sm:min-w-[980px] max-sm:w-full">
          <AgGridReact
            animateRows={false}
            onGridReady={onGridReady}
            loading={isLoading}
            onCellClicked={(event) => {
              if (event.column.getColId() === 'actions') {
                return;
              }

              if (event.data) {
                onNavigateToApplication(event.data);
              }
            }}
            onSortChanged={onSortChanged}
            rowData={evaluations}
            columnDefs={[
              {
                headerName: 'BIT Id',
                field: 'bitId',
                width: 120,
              },
              {
                headerName: 'App Name',
                field: 'applicationName',
                minWidth: 350,
                flex: 1,
                cellRenderer: renderApplicationName,
              },
              {
                headerName: 'Division',
                field: 'divisionName',
                width: 120,
              },
              {
                headerName: 'Source',
                field: 'platform',
                width: 200,
                cellRenderer: renderSource,
                sortable: false,
              },
              {
                field: 'generatedAt',
                headerName: 'Last Evaluated at',
                cellRenderer: (params: CustomCellRendererProps<Evaluation>) => {
                  const { data } = params;
                  if (!data) return <span>Not Evaluated</span>;
                  return (
                    <span>
                      {formatDistanceToNow(new Date(data.generatedAt), {
                        addSuffix: true,
                      })}
                    </span>
                  );
                },
              },
              {
                headerName: 'Last Evaluation Score',
                field: 'score',
                minWidth: 230,
                headerComponentParams: {
                  innerHeaderComponent: () => {
                    return (
                      <div className="flex items-center gap-2">
                        Last Evaluation Score
                        <span
                          id="last-evaluation-score-tooltip"
                          className="text-[#005b92]"
                        >
                          <IconHelpCircle size={16} />
                        </span>
                        <Tooltip
                          position="top"
                          targetSelector="#last-evaluation-score-tooltip"
                        >
                          <TooltipBody slot="tooltipBody">
                            ORR Score based on currently published checkpoints
                          </TooltipBody>
                        </Tooltip>
                      </div>
                    );
                  },
                },
                cellRenderer: renderScore,
              },
              {
                headerName: 'Upcoming Evaluation Score',
                field: 'inclusiveScore',
                minWidth: 230,
                headerComponentParams: {
                  innerHeaderComponent: () => {
                    return (
                      <div className="flex items-center gap-2">
                        Upcoming Evaluation Score
                        <span
                          id="upcoming-evaluation-score-tooltip"
                          className="text-[#005b92]"
                        >
                          <IconHelpCircle size={16} />
                        </span>
                        <Tooltip
                          position="top"
                          targetSelector="#upcoming-evaluation-score-tooltip"
                        >
                          <TooltipBody slot="tooltipBody">
                            ORR score including checkpoints in preview
                          </TooltipBody>
                        </Tooltip>
                      </div>
                    );
                  },
                },
                cellRenderer: renderScore,
              },
              {
                headerName: 'Total Evaluations',
                field: 'totalEvaluations',
                sortable: false,
                cellRenderer: (params: CustomCellRendererProps<Evaluation>) => {
                  const { data } = params;
                  if (!data) return <span>N/A</span>;
                  return <span>{data.totalEvaluations}</span>;
                },
              },
              {
                headerName: 'Actions',
                colId: 'actions',
                pinned: 'right',
                width: 100,
                cellRenderer: (params: CustomCellRendererProps<Evaluation>) => {
                  const id = `run-evaluation-${params.data?.id.replace(/[^a-zA-Z0-9\s]/g, '')}`;

                  return (
                    <>
                      <button
                        className="flex h-full w-full cursor-pointer items-center justify-center"
                        onClick={(event) => {
                          event.stopPropagation();
                          event.preventDefault();
                          if (params.data) {
                            onOpenRunEvaluation(params.data);
                          }
                        }}
                      >
                        <IconPlayerPlay id={id} size={16} />
                      </button>
                      <Tooltip position="left" targetSelector={`#${id}`}>
                        <TooltipBody slot="tooltipBody">
                          Trigger Evaluation
                        </TooltipBody>
                      </Tooltip>
                    </>
                  );
                },
              },
            ]}
          />
        </div>
      </div>
      <ol className="ml-auto flex w-full list-inside list-decimal flex-col gap-2 text-xs text-muted-foreground sm:flex-row sm:gap-8 lg:text-sm">
        <li>Upcoming Score is not available for historical evaluations</li>
        <li>Timeframe for when upcoming score will become current is to be decided</li>
      </ol>
    </>
  );
};

export default ApplicationEvaluationsContent;
